import React, { useCallback } from "react";
import { useField } from "formik";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useEntitiesById } from "@hooks/useEntities";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import {
    useWorkspaceAccountGroups,
    useWorkspaceAccounts,
} from "@api/account.api";
import {
    RuleGroupType,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    TransactionRuleConditionFormSchema,
    TransactionRuleActionFormSchema,
    FormRuleActionType,
} from "../../TransactionRuleForm/constants";
import { getCategoryVisibility } from "../../TransactionRuleForm/useEntityCategoryVisibility";
import { getFieldNamePrefix } from "../../utils";
import {
    clearInvalidCategoryActionForEntitySelection,
    clearInvalidCategoryConditionsForEntitySelection,
} from "../../TransactionRuleForm/RuleConditionsForm/entityConditionHelpers";
import { clearInvalidAccountActionForEntitySelection } from "../../TransactionRuleForm/RuleConditionsForm/accountActionEntityHelpers";

interface Props {
    index: number;
    groupType: RuleGroupType;
    transferDirection?: TransferRuleDirection;
}

export const TransferRuleEntityConditionForm: React.FC<Props> = ({
    index,
    groupType,
    transferDirection,
}) => {
    const categoriesById = useCategoriesById();
    const entitiesById = useEntitiesById();
    const accounts = useWorkspaceAccounts();
    const accountGroups = useWorkspaceAccountGroups();
    const showAccountNumbers = useShowAccountNumbers();
    const fieldName = `${getFieldNamePrefix(index, transferDirection)}.entityValue`;
    const [field, , helpers] =
        useField<TransactionRuleConditionFormSchema["entityValue"]>(fieldName);

    const actionsFieldName = transferDirection
        ? `actions.${transferDirection}`
        : "actions";
    const [directionActions, , directionActionsHelpers] =
        useField<TransactionRuleActionFormSchema[]>(actionsFieldName);

    const conditionsFieldName = transferDirection
        ? `conditions.${transferDirection}`
        : "conditions";
    const [conditions, , conditionsHelpers] =
        useField<TransactionRuleConditionFormSchema[]>(conditionsFieldName);

    const onChange = useCallback(
        (entityIds: number[] | undefined) => {
            const entityAction = directionActions.value?.find(
                (action) => action.actionType === FormRuleActionType.ENTITY,
            );

            const visibilityArgs = {
                entityIds: entityIds ?? [],
                entityActionEntityId: entityAction?.value?.entityId,
                entitiesById,
            };

            const actionsVisibility = getCategoryVisibility({
                ...visibilityArgs,
                context: "actions",
            });

            const conditionsVisibility = getCategoryVisibility({
                ...visibilityArgs,
                context: "conditions",
            });
            const nextConditions =
                clearInvalidCategoryConditionsForEntitySelection({
                    conditions: conditions.value,
                    categoriesById,
                    visibility: conditionsVisibility,
                });

            if (nextConditions !== conditions.value) {
                conditionsHelpers.setValue(nextConditions);
            }

            const nextActions = clearInvalidAccountActionForEntitySelection({
                actions: clearInvalidCategoryActionForEntitySelection({
                    actions: directionActions.value,
                    categoriesById,
                    visibility: actionsVisibility,
                }),
                entityIds,
                accounts: accounts.data,
                groupsByEntityId: accountGroups.data,
                showAccountNumbers,
            });

            if (nextActions !== directionActions.value) {
                directionActionsHelpers.setValue(nextActions);
            }

            helpers.setValue(entityIds);
        },
        [
            directionActions.value,
            helpers,
            categoriesById,
            entitiesById,
            directionActionsHelpers,
            accounts.data,
            accountGroups.data,
            showAccountNumbers,
            conditions.value,
            conditionsHelpers,
        ],
    );

    return (
        <EntityFilterCombobox
            filters={{ entityIds: field.value ?? [] }}
            onChange={(filters) => onChange(filters.entityIds)}
            onlyBusiness={groupType === RuleGroupType.ACCOUNTING}
            excludeEmptyMockEntities={
                ExcludeEmptyMockEntitiesMode.REQUIRE_ACCOUNTS
            }
            mutuallyExclusive
        />
    );
};
